/**
 * Helpers for the lead detail Overview edit flow.
 */

// Fields that must never be sent in a PATCH payload.
const EXCLUDED_KEYS = ["id", "createdAt", "updatedAt"];

/** Normalise a date-ish value to its YYYY-MM-DD form for comparison. */
function normalizeDate(value: unknown): string {
  if (value === null || value === undefined || value === "") return "";
  return String(value).substring(0, 10);
}

/**
 * Returns only the fields the user actually changed in the edit form.
 *
 * `original` is the lead snapshot taken when editing began and `edited` is the
 * current form state (seeded from that same snapshot). A key is included only
 * when the user changed it in the form (`edited[key] !== original[key]`), so a
 * value saved elsewhere (e.g. the Notes tab) after editing started is never
 * clobbered. `dateOfLoss` is compared by its YYYY-MM-DD value so an unchanged
 * date stored in a different representation isn't sent. `id`/`createdAt`/
 * `updatedAt` are never included.
 */
export function changedLeadFields<T extends Record<string, any>>(
  original: Partial<T> | null | undefined,
  edited: Partial<T> | null | undefined,
): Partial<T> {
  const changes: Partial<T> = {};
  if (!edited) return changes;
  const base: Partial<T> = original ?? {};

  for (const key of Object.keys(edited) as Array<keyof T & string>) {
    if (EXCLUDED_KEYS.includes(key)) continue;

    if (key === "dateOfLoss") {
      if (normalizeDate(edited[key]) !== normalizeDate(base[key])) {
        changes[key] = edited[key];
      }
      continue;
    }

    if (edited[key] !== base[key]) {
      changes[key] = edited[key];
    }
  }

  return changes;
}
